import { NgIf, AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';
import { RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

import { DiscountNavigationService } from '../../services/discount-navigation.service';
import { FinalSetupNavigationService } from '../../services/final-setup-navigation.service';
import { getSelectedCycleId } from '../../store/selectors/cycle.selectors';

@Component({
  selector: 'app-cycle-navigation',
  templateUrl: 'cycle-navigation.component.html',
  imports: [NgIf, MatTabNav, MatTabLink, RouterLink, MatTabNavPanel, AsyncPipe, TranslatePipe],
})
export class CycleNavigationComponent {
  private readonly store = inject<Store<RootState>>(Store);
  private discountNavigationService = inject(DiscountNavigationService);
  private finalSetupNavigationService = inject(FinalSetupNavigationService);

  cycleId$: Observable<number> = this.store.select(getSelectedCycleId);

  routeUrl$: Observable<string> = this.store.pipe(select(RouterSelectors.getPath));

  canNavigate$: Observable<boolean> = this.discountNavigationService.canNavigate();

  canNavigateFinalSetup$: Observable<boolean> = this.finalSetupNavigationService.canNavigate();

  isDayOldChick$: Observable<boolean> = this.routeUrl$.pipe(
    map((url) => url.indexOf('day-old-chick') > 0),
  );

  isDeparture$: Observable<boolean> = this.routeUrl$.pipe(
    map((url) => url.indexOf('departure') > 0),
  );

  isBalance$: Observable<boolean> = this.routeUrl$.pipe(map((url) => url.indexOf('balance') > 0));

  isClosure$: Observable<boolean> = this.routeUrl$.pipe(map((url) => url.indexOf('closure') > 0));

  isDiscount$: Observable<boolean> = this.routeUrl$.pipe(map((url) => url.indexOf('discount') > 0));

  isAnimalGroup$: Observable<boolean> = this.routeUrl$.pipe(
    map((url) => url.indexOf('animal-group') > 0),
  );

  isFinalSetup$: Observable<boolean> = this.routeUrl$.pipe(
    map((url) => url.indexOf('final-setup') > 0),
  );

  isGeneral$: Observable<boolean> = this.isDayOldChick$.pipe(
    withLatestFrom(
      this.isDeparture$,
      this.isBalance$,
      this.isClosure$,
      this.isDiscount$,
      this.isAnimalGroup$,
      this.isFinalSetup$,
    ),
    map(
      ([
        isDayOldChick,
        isDeparture,
        isBalance,
        isClosure,
        isDiscount,
        isAnimalGroup,
        isFinalSetup,
      ]) =>
        !(
          isDayOldChick ||
          isDeparture ||
          isBalance ||
          isClosure ||
          isDiscount ||
          isAnimalGroup ||
          isFinalSetup
        ),
    ),
  );

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}
}
